export const DIALOGUES_PAGE_SIZE = 50;
export const INFINITE_SCROLL_LIMIT = 100;

interface DialoguesPagingInput {
  selectedVacancyId?: string | number | null;
  loading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
  loadedCount: number;
  searchQuery: string;
}

export interface DialoguesPaginationView {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  isBlockPagination: boolean;
  canLoadMore: boolean;
  showPagination: boolean;
}

export interface DialoguesRefreshRequest {
  page: number;
  size: number;
}

const hasSearch = (searchQuery: string): boolean => Boolean(searchQuery.trim());

const toBlockPage = (page: number, pageSize: number): number => {
  if (pageSize === INFINITE_SCROLL_LIMIT) {
    return page;
  }
  return Math.floor((page * pageSize) / INFINITE_SCROLL_LIMIT);
};

export const getDialoguesPaginationView = ({
  selectedVacancyId,
  loading,
  page,
  pageSize,
  totalPages,
  totalElements,
  loadedCount,
  searchQuery,
}: DialoguesPagingInput): DialoguesPaginationView => {
  const hasVacancy = Boolean(selectedVacancyId);
  const searchActive = hasSearch(searchQuery);
  const isBlockPagination = hasVacancy
    && !searchActive
    && (loadedCount >= INFINITE_SCROLL_LIMIT || pageSize === INFINITE_SCROLL_LIMIT);
  const blockTotalPages = Math.ceil(totalElements / INFINITE_SCROLL_LIMIT);
  const currentPage = isBlockPagination ? toBlockPage(page, pageSize) : page;
  const uiTotalPages = isBlockPagination ? blockTotalPages : totalPages;

  return {
    currentPage,
    totalPages: uiTotalPages,
    pageSize: isBlockPagination ? INFINITE_SCROLL_LIMIT : DIALOGUES_PAGE_SIZE,
    isBlockPagination,
    canLoadMore:
      hasVacancy
      && !loading
      && !searchActive
      && !isBlockPagination
      && page + 1 < totalPages
      && loadedCount < INFINITE_SCROLL_LIMIT,
    showPagination:
      hasVacancy
      && uiTotalPages > 1
      && (isBlockPagination || searchActive),
  };
};

export const getDialoguesRefreshRequest = ({
  page,
  pageSize,
  loadedCount,
  searchQuery,
}: Pick<DialoguesPagingInput, 'page' | 'pageSize' | 'loadedCount' | 'searchQuery'>): DialoguesRefreshRequest => {
  if (hasSearch(searchQuery)) {
    return { page, size: DIALOGUES_PAGE_SIZE };
  }

  if (loadedCount >= INFINITE_SCROLL_LIMIT || pageSize === INFINITE_SCROLL_LIMIT) {
    return {
      page: toBlockPage(page, pageSize),
      size: INFINITE_SCROLL_LIMIT,
    };
  }

  return {
    page: 0,
    size: Math.min(Math.max(loadedCount, DIALOGUES_PAGE_SIZE), INFINITE_SCROLL_LIMIT),
  };
};
